import React, { useState, useEffect } from 'react';
import { Table, message } from 'antd';
import { useParams } from 'react-router-dom';
import {
  EmptyModelVisibleState,
  IModel,
  IModelSlave,
  IProject,
} from 'utils/types/storyTypes';
import SearchInput from 'components/search/SearchInput';
import TableToolbar from 'components/table/TableToolbar';
import MainTable from 'components/table/MainTable';
import DefaultButton from 'components/button/DefaultButton';
import { theme } from 'utils/style/theme';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { handleMessage, showApiErrorMessage } from 'utils/helper/message';
import { modelAPI } from 'apis/resource/modelAPI';
import ActionButton from 'components/table/ActionButton';
import {
  AddSquare,
  ArrowDown2,
  ArrowUp2,
  Edit2,
  PlayCricle,
  RefreshSquare,
  Trash,
} from 'iconsax-react';
import ModelMotionRegisterModal from './ModelMotionRegisterModal';
import ModelRegisterModal from './ModelRegisterModal';
import DeleteModal from 'components/modal/DeleteModal';
import ModelUpdateModal from './ModelUpdateModal';
import ModelMotionUpdateModal from './ModelMotionUpdateModal';
import {
  LIVE2D_DOWNLOADER_URL,
  LIVE2D_VIEWER_URL,
  ifyouApi,
} from 'apis/config';
/**
 * * 리소스 - 캐릭터 모델 메인
 */

interface IProps {
  currentProject: IProject;
}

const ModelTable = ({ currentProject }: IProps) => {
  const { t } = useTranslation();
  const { projectId } = useParams(); // id: 프로젝트 id
  const [searchData, setSearchData] = useState<string>(''); // 검색어
  const [visibleState, setVisibleState] = useState(EmptyModelVisibleState); // 모달 show on/off

  const [currentModel, setCurrentModel] = useState<IModel | null>(null);
  const [currentSlave, setCurrentSlave] = useState<IModelSlave | null>(null);

  const [refresh, setRefresh] = useState<boolean>(false);

  const [modelList, setModelList] = useState<IModel[]>([]);
  const [tableData, setTableData] = useState<IModel[]>([]);

  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  const handleFetchData = async () => {
    if (projectId) {
      try {
        const res = await modelAPI.getModelList(projectId);
        setModelList(res.list);
      } catch (err) {
        console.log('fetch data error: ', err);
        showApiErrorMessage(err);
      }
    }
  };

  useEffect(() => {
    if (modelList) {
      setTableData(modelList);
    }
  }, [modelList, currentSlave]);

  useEffect(() => {
    handleFetchData();
  }, [refresh]);

  // handle search input
  useEffect(() => {
    if (searchData && searchData.length) {
      const filterData = modelList.filter((e) =>
        e.model_name.toLowerCase().includes(searchData.toLowerCase()),
      );
      setTableData(filterData);
    } else {
      setTableData(modelList);
    }
  }, [searchData]);

  const handleClickDeleteModal = (record) => {
    setCurrentModel(record);
    setVisibleState({
      ...visibleState,
      modelDelete: true,
      name: record?.model_name,
    });
  };

  const handleClickResetModal = (record) => {
    console.log('handleClickResetModal');

    setCurrentModel(record);
    setVisibleState({
      ...visibleState,
      modelReset: true,
      name: record?.model_name,
    });
  };

  // * 모션 플레이 버튼 클릭
  const handleClickMotion = async (record) => {
    setCurrentModel(record);
    console.log(`handleClickMotion : `, record);

    if (!record.slaves) {
      handleMessage('파일이 등록되지 않음', 'Error', '파일이 등록되지 않음');
      return;
    }

    const downloadResult = await ifyouApi.post(LIVE2D_DOWNLOADER_URL, {
      list: record.slaves,
      isCharacter: true,
    });

    if (downloadResult.status != 200) {
      handleMessage('Download Fail', 'Error', 'Please try again');
      return;
    }
    console.log('download result : ', downloadResult.data);

    let newFileKey;
    record.slaves?.map((v) => {
      if (v.file_key?.indexOf('.model3.json') !== -1) {
        newFileKey = v;
        return newFileKey;
      }

      return null;
    });

    console.log(`model key : `, newFileKey);
    if (!newFileKey) {
      handleMessage('No Main File in Live2D', 'Error', 'Check Model');
      return;
    }

    const motionView = `${LIVE2D_VIEWER_URL}?modelid=${record.model_id}&modelinfo=${newFileKey.file_key}`;
    console.log(`view url : `, motionView);
    // 오픈
    window.open(motionView, '_blank'); //motionview 새탭열기
  };

  const handleReset = async () => {
    console.log(`handle Reset!`);

    if (currentModel) {
      try {
        setLoading(true);
        await modelAPI.characterModelReset(
          currentModel.project_id,
          currentModel.model_id,
        );
        setRefresh(!refresh);
        setLoading(false);
        setVisibleState({ ...visibleState, modelReset: false });
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Please try again');
        console.log('Reset error:', err);
      }
    }
  };

  const [loading, setLoading] = useState<boolean>(false);

  // 모델 삭제
  const handleModelDelete = async () => {
    if (currentModel) {
      try {
        setLoading(true);
        await modelAPI.characterModelDelete(
          currentModel.project_id,
          currentModel.model_id,
        );
        setRefresh(!refresh);
        setLoading(false);
        setVisibleState({ ...visibleState, modelDelete: false });
      } catch (err) {
        setLoading(false);
        handleMessage(message, 'error', 'Please try again');
        console.log('Delete error:', err);
      }
    }
  };

  const expandedRowRender = (record) => {
    return (
      <MainTable
        columns={columnsNested}
        dataSource={record.slaves}
        isPagination={false}
      />
    );
  };

  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <AddSquare
            size='24'
            onClick={() => {
              setCurrentModel(record);
              setVisibleState({ ...visibleState, assetsRegister: true });
            }}
          />
        ),
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: (
          <PlayCricle
            size='24'
            onClick={() => {
              console.log('click play circle');

              handleClickMotion(record);
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },
      {
        key: 3,
        item: (
          <RefreshSquare
            size='24'
            onClick={() => {
              handleClickResetModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },
      {
        key: 4,
        item: (
          <Edit2
            size='24'
            onClick={() => {
              setCurrentModel(record);
              setVisibleState({
                ...visibleState,
                modelUpdate: true,
                name: record?.model_name,
              });
            }}
          />
        ),
        hoverColor: theme.colors.blue_500,
        color: theme.colors.dark_200,
      },

      {
        key: 5,
        item: (
          <Trash
            size='24'
            onClick={() => {
              handleClickDeleteModal(record);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  // 서브 그리드의 액션버튼 => 모션 입력
  const tableActionNestMenu = (record) => {
    if (record?.file_name && record.file_name.includes('motion3')) {
      return [
        {
          key: 1,
          item: (
            <Edit2
              size='24'
              onClick={() => {
                // 모션 입력 창 띄우기
                setCurrentSlave(record);

                // console.log(`서브 그리드 클릭 : `, currentSlave);

                setVisibleState({ ...visibleState, assetsUpdate: true });
              }}
            />
          ),
          hoverColor: theme.colors.blue_500,
          color: theme.colors.dark_200,
        },
      ];
    } else {
      return [];
    }
  };

  // 테이블 설정
  const columnsOrigin = [
    {
      title: 'ID',
      dataIndex: 'model_id',
      width: 'fit-content',
      align: 'center',
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: '모델명',
      dataIndex: 'model_name',
      width: '9rem',
    },
    {
      title: '인게임 높이',
      dataIndex: 'offset_y',
      width: '7rem',
      align: 'center',
    },
    {
      title: '인게임 크기',
      dataIndex: 'game_scale',
      width: '7rem',
      align: 'center',
    },
    {
      title: '로비 높이',
      dataIndex: 'lobby_offset_y',
      width: '7rem',
      align: 'center',
    },
    {
      title: '로비 크기',
      dataIndex: 'lobby_game_scale',
      width: '7rem',
      align: 'center',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.characterContent.table.gaze',
      ),
      dataIndex: 'direction',
      width: '7rem',
      align: 'center',
    },
    {
      title: '',
      dataIndex: '',
      width: '4rem',
      render: (text, record) => {
        const menus = [tableActionMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
    Table.EXPAND_COLUMN,
  ];

  const columnsNested = [
    {
      title: 'ID',
      dataIndex: 'model_slave_id',
      width: 'fit-content',
      align: 'center',
    },
    {
      title: '파일명',
      dataIndex: 'file_name',
      width: '9rem',
    },
    {
      title: t(
        'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.characterContent.table.motion',
      ),
      dataIndex: 'motion_name',
      width: '7rem',
    },
    {
      title: '',
      dataIndex: '',
      width: '4rem',
      render: (text, record) => {
        const menus = [tableActionNestMenu(record)];
        return <ActionButton menus={menus} />;
      },
    },
  ];

  return (
    <>
      <TableToolbar>
        <SearchInput placeholder='Search...' setSearchData={setSearchData} />
        <div>
          <DefaultButton
            name='register'
            onClick={() =>
              setVisibleState({ ...visibleState, modelRegister: true })
            }
          >
            {t(
              'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.characterContent.header.createModelButton',
            )}
          </DefaultButton>
        </div>
      </TableToolbar>
      <TableWrapper>
        <MainTable
          rowKey='model_id'
          rowClassName={(record) => {
            if (record && record.model_id) {
              if (expandedRows.includes(record.model_id)) {
                return 'active';
              }
            }
          }}
          isExpand
          columns={columnsOrigin}
          dataSource={tableData}
          expandable={{
            expandedRowRender: (record) => {
              return expandedRowRender(record);
            },
            expandIcon: ({ expanded, onExpand, record }) => {
              // Customize the expand column icon based on the 'expanded' state
              return expanded ? (
                <ArrowUp2
                  size='20'
                  color={theme.colors.dark_200}
                  onClick={(e) => onExpand(record, e)}
                />
              ) : (
                <ArrowDown2
                  size='20'
                  color={theme.colors.dark_200}
                  onClick={(e) => onExpand(record, e)}
                />
              );
            },
            onExpandedRowsChange: (expandedRows) => {
              setExpandedRows(expandedRows);
            },
          }}
        />
      </TableWrapper>

      {/* 모달 모음 */}
      <ModelMotionRegisterModal
        visible={visibleState}
        setVisible={setVisibleState}
        setRefresh={setRefresh}
        refresh={refresh}
        data={currentModel}
      />
      {/* 모달 모음 */}
      <ModelRegisterModal
        visibleState={visibleState}
        setVibileState={setVisibleState}
        setRefresh={setRefresh}
        refresh={refresh}
      />
      <DeleteModal
        visible={visibleState.modelDelete}
        setVisible={(e) => {
          setVisibleState({ ...visibleState, modelDelete: e });
        }}
        title={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.characterContent.deleteModelModal.title',
        )}
        label={visibleState.name}
        handleDelete={handleModelDelete}
        loading={loading}
      />
      <DeleteModal
        visible={visibleState.modelReset}
        setVisible={(e) => {
          setVisibleState({ ...visibleState, modelReset: e });
        }}
        title={t(
          'storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.characterContent.resetModelModal.title',
        )}
        content='storyEpisodeID.storyTabMenuList.resourceContent.resourceTabMenuList.characterContent.resetModelModal.content'
        label={visibleState.name}
        handleDelete={handleReset}
        loading={loading}
      />
      <ModelUpdateModal
        visible={visibleState.modelUpdate}
        setVisible={(e) => {
          setVisibleState({ ...visibleState, modelUpdate: e });
        }}
        selectedData={currentModel}
        setSelectedData={setCurrentModel}
        setRefresh={setRefresh}
        refresh={refresh}
      />
      <ModelMotionUpdateModal
        visible={visibleState.assetsUpdate}
        setVisible={(e) => {
          setVisibleState({ ...visibleState, assetsUpdate: e });
        }}
        currentModel={currentModel}
        setCurrentModel={setCurrentModel}
        setRefresh={setRefresh}
        refresh={refresh}
        currentSlave={currentSlave}
        setCurrentSlave={setCurrentSlave}
      />
    </>
  );
};

export default ModelTable;

const TableWrapper = styled.div`
  .ant-table table tr {
    vertical-align: top;
  }
  .ant-table-thead > tr > th {
    padding: 0.8rem 0.8rem;
  }
  .ant-table-thead > tr > th,
  .ant-table-column-title {
    font-size: ${theme.fontSizes.normal};
    color: ${theme.colors.dark_300} !important;
    font-weight: ${theme.fontWeights.bold};
    line-height: 16px;
    letter-spacing: 0.006rem;
  }
  .ant-table-wrapper
    .ant-table-tbody
    > tr
    > td
    > .ant-table-wrapper:only-child
    .ant-table {
    margin: 0 10px;
  }
`;
